import { RimBoardAlignment } from "building-types/floors/floorComponentsTypes";
import type { PerEdgeRimStackInfo, RimAlignmentOffsets } from "building-types/floors/floorRimAlignmentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { EdgeMetadata, EdgeMetadataTable } from "building-types/framing/framingMetaDataTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type { Table } from "common/data/table";
import { getFramingProfileDescriptorById } from "doc/utils/lumberTools";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { offsetPolyLine2, offsetPolyLine2VariableStep } from "geom/offset";
import { polyLine2ByTwoPoints, polyLine2Copy, polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export function computeRimAlignmentOffsets(alignment: RimBoardAlignment, stackDepth: number): RimAlignmentOffsets {
  switch (alignment) {
    case RimBoardAlignment.CENTER: {
      const half = stackDepth / 2;
      return {
        outerRimInnerFaceInset: half,
        joistPerimeterInset: half,
        subfloorOutwardExpansion: half,
      };
    }
    case RimBoardAlignment.EXTERIOR:
      return {
        outerRimInnerFaceInset: stackDepth,
        joistPerimeterInset: stackDepth,
        subfloorOutwardExpansion: 0,
      };
    case RimBoardAlignment.INTERIOR:
    default:
      return {
        outerRimInnerFaceInset: 0,
        joistPerimeterInset: 0,
        subfloorOutwardExpansion: stackDepth,
      };
  }
}

export function resolvePerEdgeRimStackInfo(
  edgeMetadata: EdgeMetadata | undefined,
  defaultRimProfile: FramingProfileDescriptor,
  framingComponents?: Table<FramingComponentEntity>,
): PerEdgeRimStackInfo | undefined {
  if (edgeMetadata?.splitLineThickness !== undefined) {
    return undefined;
  }

  const framingConfig = edgeMetadata?.framingConfig;
  if (framingConfig?.disabled) {
    return undefined;
  }

  let profileHeight = defaultRimProfile.height;
  if (framingConfig?.framingComponentId && framingComponents) {
    const overrideDescriptor = getFramingProfileDescriptorById(framingComponents, framingConfig.framingComponentId);
    if (overrideDescriptor) {
      profileHeight = overrideDescriptor.height;
    }
  }

  const framingCount = Math.max(1, framingConfig?.count ?? 1);
  return {
    stackDepth: framingCount * profileHeight,
    profileHeight,
    framingCount,
  };
}

/**
 * Offset a rim rail edge relative to the user boundary.
 * Positive inset values move inward from the boundary (toward floor interior).
 *
 * One stacking rule for all alignments: stackIndex 0 is at `outerRimInnerFaceInset`
 * (joist-abutment face); higher indices stack outward by `profileHeight`.
 *
 * INTERIOR: ply1-ply0-Boundary-joists
 *  CENTER:  ply1-Boundary-ply0-joists
 *  EXTERIOR: Boundary-ply1-ply0-joists
 *
 * Note: open-edge offset alone leaves gaps at reflex corners for CENTER/EXTERIOR. Callers that
 * need a continuous rim loop should pass the results through {@link miterConsecutiveRimRails}.
 */
export function offsetRimRailFromBoundary(
  edgeRail: PolyLine2,
  stackIndex: number,
  profileHeight: number,
  framingCount: number,
  alignment: RimBoardAlignment,
): PolyLine2 {
  const stackDepth = framingCount * profileHeight;
  const { outerRimInnerFaceInset } = computeRimAlignmentOffsets(alignment, stackDepth);

  if (stackIndex === 0) {
    if (outerRimInnerFaceInset === 0) {
      return polyLine2Copy(edgeRail);
    }
    return offsetPolyLine2(polyLine2Copy(edgeRail), -outerRimInnerFaceInset);
  }

  const baseRail =
    outerRimInnerFaceInset === 0
      ? polyLine2Copy(edgeRail)
      : offsetPolyLine2(polyLine2Copy(edgeRail), -outerRimInnerFaceInset);
  return offsetPolyLine2(baseRail, stackIndex * profileHeight);
}

/**
 * Miter consecutive rim rails by moving shared endpoints to the supporting-line intersection.
 *
 * Independently offset open edges do not meet at reflex corners (intersection lies past both
 * endpoints) and leave T-gaps at convex corners for CENTER/EXTERIOR. Mitering makes the rim loop
 * continuous so DCEL connectivity and clip planes work at every corner.
 *
 * Entries may be `undefined` for disabled / split-line edges; those gaps are left unmitered.
 */
export function miterConsecutiveRimRails(rails: (PolyLine2 | undefined)[]): (PolyLine2 | undefined)[] {
  const count = rails.length;
  if (count < 2) {
    return rails.map((rail) => (rail ? polyLine2Copy(rail) : undefined));
  }

  const miterPoints: ({ x: number; y: number } | undefined)[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const curr = rails[i];
    const next = rails[(i + 1) % count];
    if (!curr || !next) {
      continue;
    }

    const intersection = intersectLine2Line2(
      curr[0],
      curr[1],
      curr[2],
      curr[3],
      next[0],
      next[1],
      next[2],
      next[3],
      undefined,
      undefined,
      false,
    );
    if (!(intersection instanceof CurveCurvePointIntersection)) {
      continue;
    }

    miterPoints[i] = { x: intersection.x, y: intersection.y };
  }

  return rails.map((rail, i) => {
    if (!rail) {
      return undefined;
    }
    const start = miterPoints[(i - 1 + count) % count];
    const end = miterPoints[i];
    if (!start && !end) {
      return polyLine2Copy(rail);
    }
    return polyLine2ByTwoPoints(start?.x ?? rail[0], start?.y ?? rail[1], end?.x ?? rail[2], end?.y ?? rail[3]);
  });
}

export function computePerEdgeJoistPerimeterInsets(
  edgeCount: number,
  edgeMetadata: EdgeMetadataTable | undefined,
  alignment: RimBoardAlignment,
  defaultRimProfile: FramingProfileDescriptor,
  framingComponents?: Table<FramingComponentEntity>,
): number[] {
  const insets: number[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const meta = edgeMetadata?.[i];
    if (meta?.splitLineThickness !== undefined || meta?.framingConfig?.disabled) {
      insets.push(0);
      continue;
    }

    const stackInfo = resolvePerEdgeRimStackInfo(meta, defaultRimProfile, framingComponents);
    const stackDepth = stackInfo?.stackDepth ?? defaultRimProfile.height;
    insets.push(computeRimAlignmentOffsets(alignment, stackDepth).joistPerimeterInset);
  }
  return insets;
}

export function insetPerimeterForJoists(
  perimeter: PolyLine2,
  edgeMetadata: EdgeMetadataTable | undefined,
  alignment: RimBoardAlignment,
  defaultRimProfile: FramingProfileDescriptor,
  framingComponents?: Table<FramingComponentEntity>,
): PolyLine2 {
  const edgeCount = polyLine2EdgeCount(perimeter);
  const insets = computePerEdgeJoistPerimeterInsets(
    edgeCount,
    edgeMetadata,
    alignment,
    defaultRimProfile,
    framingComponents,
  );

  if (insets.every((v) => v === 0)) {
    return perimeter;
  }

  const { pl } = offsetPolyLine2VariableStep(
    polyLine2Copy(perimeter),
    insets.map((inset) => -inset),
    true,
  );
  return pl;
}

export function computePerEdgeSubfloorOutwardExpansions(
  edgeCount: number,
  edgeMetadata: EdgeMetadataTable | undefined,
  alignment: RimBoardAlignment,
  defaultRimProfile: FramingProfileDescriptor,
  framingComponents?: Table<FramingComponentEntity>,
): number[] {
  const expansions: number[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const meta = edgeMetadata?.[i];

    if (meta?.splitLineThickness !== undefined) {
      expansions.push(meta.splitLineThickness / 2);
      continue;
    }

    if (meta?.framingConfig?.disabled) {
      expansions.push(0);
      continue;
    }

    const stackInfo = resolvePerEdgeRimStackInfo(meta, defaultRimProfile, framingComponents);
    const stackDepth = stackInfo?.stackDepth ?? defaultRimProfile.height;
    expansions.push(computeRimAlignmentOffsets(alignment, stackDepth).subfloorOutwardExpansion);
  }
  return expansions;
}
